import { database } from '../config/database.js';
import { DatabaseEvent } from './database_event.js';
import { defineAttributes, uniq, objectDiff } from './utils.js';


class ModelEvent extends Event {
  constructor({ details, scope }) {
    super('model-event');
    this.scope = scope;
    this.details = details;
  }
}

const ORDER = {
  desc: 'prev',
  asc: 'next',
  prev: 'prev',
  next: 'next',
};

function getOrder(name) {
  const order = ORDER[name];
  if (!order) throw new Error(`Invalid order: ${name}`);

  return order;
}

export class ActiveRecord {
  static db = database;
  static primaryKey = 'id';
  static eventTarget = new EventTarget();
  static defaultValues = {};

  constructor(attributes) {
    this.attributes = {};
    this.isDirty = false;
    this.changes = new Map();
    const v = Object.assign({}, this.constructor.defaultValues, attributes)
    // console.log('oo', v);
    Object.assign(this, v);
    this.changes.clear();
  }

  static async before() {}
  static async after() {}


  static attrAccessor(...attributes) {
    // Skip attributes that are already defined
    const attributesToDefine = uniq([this.primaryKey, ...attributes]);
    const undefinedAttributes = attributesToDefine.filter(
      (attr) => !this.prototype[attr]
    );
    // console.log(attributesToDefine, undefinedAttributes, this.primaryKey)
    defineAttributes(this, undefinedAttributes);
    return attributes;
  }

  static async truncate() {
    const result = await this.db.clear(this.tableName);
    this.requestUpdate('truncate');
    return result;
  }

  static deleteAll() {
    return this.truncate();
  }

  static dispatchEvent(actions) {
    this.eventTarget.dispatchEvent(
      new DatabaseEvent({
        scope: this.tableName,
        actions,
      })
    );
  }


  // BATCH / FLUSH UPDATES START

  static pendingUpdate = false;
  static pendingUpdates = [];

  static requestUpdate(method, details) {
    this.pendingUpdates.push({ method, details });
    if (!this.pendingUpdate) {
      this.pendingUpdate = true;
      requestIdleCallback(() => this.flushUpdates());
    }
  }

  static flushUpdates() {
    this.eventTarget.dispatchEvent(
      new ModelEvent({
        scope: this.tableName,
        details: this.pendingUpdates,
      })
    );
    this.dispatchEvent(this.pendingUpdates);
    this.pendingUpdate = false;
    this.pendingUpdates = [];
  }

  // BATCH / FLUSH UPDATES END

  static async dbAdd(attributes) {
    const id = attributes[this.primaryKey];
    const castedAttributes = this.castToDatabase(attributes);

    return this.db.add(this.tableName, castedAttributes, this.externalId ? id : undefined);
  }

  static async dbPut(attributes) {
    const id = attributes[this.primaryKey];
    const castedAttributes = this.castToDatabase(attributes);

    return this.db.put(this.tableName, castedAttributes, this.externalId ? id : undefined);
  }


  static async create(attributes) {
    return new this(attributes).create();
  }

  static async update(attributes) {
    const id = attributes[this.primaryKey];
    const current = await this.find(id);

    return current.update(attributes);
  }

  static async upsert(attributes) {
    const id = attributes[this.primaryKey];
    const savedId = await this.db.put(this.tableName, this.castToDatabase(attributes), id);
    this.requestUpdate('update');
    return savedId;
  }

  static async upsertAll(records) {
    const tx = this.db.transaction(this.tableName, 'readwrite');
    for (const record of records) {
      tx.store.put(this.castToDatabase(record), this.externalId ? record[this.primaryKey] : undefined);
    }
    await tx.done;
    this.requestUpdate('update');
    return true;
  }

  static async upsertAllInBatches(records, {batchSize = 5000} = {}) {
    const total = records.length;
    let count = 0;
    while (count < total) {
      const batch = records.slice(count, count + batchSize);
      await this.upsertAll(batch);
      count += batchSize;
    }
    return true;
  }

  // This will not throw an error if the record does not exist
  static async destroy(id, {skipCallbacks = false} = {}) {
    if(!skipCallbacks) await this.before('destroy', this);
    await this.db.delete(this.tableName, id);
    if(!skipCallbacks) await this.after('destroy', this);
    this.requestUpdate('destroy');
    return true;
  }

  static async safeFind(id) {
    try {
      return await this.find(id);
    } catch (error) {
      return null;
    }
  }

  static async find(id) {
    const row = await this.db.get(this.tableName, id);
    if (!row)
      throw new Error(`Record '${id}' not found in table '${this.tableName}'`);

    return this.fromDatabase(row);
  }

  static async findOrInitialize(id, attributes) {
    return await this.safeFind(id) || this.new({...attributes, [this.primaryKey]: id})
  }

  static async findOrCreate(id, attributes) {
    return await this.safeFind(id) || await this.create({...attributes, [this.primaryKey]: id})
  }

  static async count() {
    return this.db.count(this.tableName);
  }

  static async countFromIndex(index, query) {
    return this.db.countFromIndex(this.tableName, index, query);
  }

  static fromDatabase(row) {
    const castedRow = this.castFromDatabase(row);
    const instance = new this(castedRow);
    instance.saved = true;
    instance.loaded = true;
    return instance;
  }

  // Convert key that starts with is to boolean
  // Because IndexedDB does not support boolean type for indexes
  static castFromDatabase(row) {
    const attributes = {...row};

    for(const [key, value] of Object.entries(row)) {
      if (key.startsWith('is')) {
        attributes[key] = Boolean(value);
      }
    }

    return attributes;
  }

  static castToDatabase(attributes) {
    const row = {...attributes };

    for(const [key, value] of Object.entries(attributes)) {
      const nullOrUndefined = value === null || value === undefined;
      if (key.startsWith('is') && typeof value === 'boolean') {
        row[key] = value ? 1 : 0;
      } else if (key.endsWith('At') && !(value instanceof Date) && !nullOrUndefined) {
        row[key] = new Date(value);
      }
    }

    return row;
  }

  static wrapAll(rows) {
    const items = rows.map((row) => {
      const instance = this.fromDatabase(row);
      return [instance.primaryKey, instance];
    });

    return new Map(items);
  }

  static async all({ index, query, filter, limit, order } = {}) {
    let rows;
    if (index) {
      // rows = await this.db.getAllFromIndex(this.tableName, index, query, limit);
      rows = await this.getAllFromIndexAdvanced({
        index,
        query,
        filter,
        limit,
        order,
      });
    } else {
      rows = await this.db.getAll(this.tableName, query, limit);
    }
    return this.wrapAll(rows);
  }

  static async first(options) {
    const row = await this.all({...options, limit: 1 });
    return row.values().next().value;
  }

  static async getAllFromIndexAdvanced({ index, query, filter, limit, order }) {
    // console.log(
    //   'getAllFromIndexAdvanced',
    //   this.tableName,
    //   index,
    //   query,
    //   filter,
    //   limit,
    //   order
    // );
    let cursor = await this.db
      .transaction(this.tableName)
      .store.index(index)
      .openCursor(query, order ? getOrder(order) : undefined);

    const rows = [];
    while (cursor) {
      if (filter && !filter(cursor.value)) {
        cursor = await cursor.continue();
        continue;
      }

      rows.push(cursor.value);
      if (limit && rows.length >= limit) break;

      cursor = await cursor.continue();
    }

    return rows;
  }

  static async cursorFilter(callback) {
    const tx = this.db.transaction(this.tableName);

    const rows = [];
    for await (const cursor of tx.store) {
      if (callback(cursor.value)) rows.push(cursor.value);
    }

    return this.wrapAll(rows);
  }

  static async dump() {
    const data = await this.db.getAll(this.tableName);
    // return JSON.stringify(data, null, 2);
    return data;
  }

  static async dumpToJson() {
    const data = await this.dump();
    return JSON.stringify(data, null, 2);
  }

  get primaryKey() {
    return this.attributes[this.constructor.primaryKey];
  }

  setPrimaryKey(value) {
    this[this.constructor.primaryKey] = value;
  }

  async create(attributes, {skipCallbacks = false} = {}) {
    if (this.saved) return this;

    const time = new Date();

    this.createdAt = time;
    this.updatedAt = time;

    if(!skipCallbacks) await this.constructor.before('create', this);
    const id = await this.constructor.dbAdd(this.attributes);
    this.saved = true;
    this.isDirty = false;

    id && this.setPrimaryKey(id);

    if(!skipCallbacks) await this.constructor.after('create', this);

    this.requestUpdate('create');

    return this;
  }

  async update(attributes, {skipCallbacks = false} = {}) {
    this.assign(attributes);

    if(!this.isDirty) return this;
    if(!skipCallbacks) await this.constructor.before('update', this);

    this.updatedAt = new Date();

    await this.constructor.dbPut(this.attributes);

    this.saved = true;
    this.isDirty = false;

    if(!skipCallbacks) await this.constructor.after('update', this);

    this.requestUpdate('update', {
      id: this.primaryKey,
      attributes: objectDiff(this.attributes, attributes),
    });

    this.changes.clear();
    return this;
  }

  save() {
    return this.update(this.attributes);
  }



  static getRaw(id) {
    return this.db.get(this.tableName, id);
  }

  async reload() {
    const attributes = await this.constructor.getRaw(this.primaryKey);
    console.log('reload', attributes);
    this.assign(attributes);
    this.saved = true;

    return this;
  }

  async reFind() {
    return this.constructor.find(this.primaryKey);
  }

  load() {
    return !this.saved && this.reload();
  }

  assign(attributes) {
    Object.assign(this, attributes);
    return this;
  }

  destroy() {
    return this.constructor.destroy(this.primaryKey);
  }

  inspectAttributes() {
    return JSON.stringify(this.attributes)
      .replace(/["{}]/g, '')
      .replace(/,/g, ', ');
  }

  toString() {
    return `<${this.constructor.name} ${this.inspectAttributes()}>`;
  }

  inspect() {
    return this.toString();
  }

  requestUpdate(method) {
    this.constructor.requestUpdate(method);
  }

  without(...keys) {
    const slicedAttributes = {};
    for (const key in this.attributes) {
      if (!keys.includes(key)) slicedAttributes[key] = this.attributes[key];
    }
    return slicedAttributes;
  }
}

